import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  Button,
} from '@mui/material'
import toast from 'react-hot-toast'
import {
  ConfigurationSettings,
  SchedulerSettingsSnapshot,
  useScheduleSettings,
  useUpdateConfigurationSettings,
} from './Scheduler.data'

interface ScheduleSettingsSnapshotProps {
  schedulerId: string
  iteration: string
}

// Categorisation matches the user's mental model + the comments in
// SchedulerRunConfig. Each entry is [label, key, formatter].
type FieldDef = {
  label: string
  key: keyof SchedulerSettingsSnapshot
  format?: (v: unknown) => string
}

const fmtNumber = (v: unknown) =>
  v === null || v === undefined ? '—' : String(v)
const fmtBool = (v: unknown) => (v === true ? 'Yes' : v === false ? 'No' : '—')
const fmtList = (v: unknown) => {
  if (!v) return '—'
  try {
    const parsed = JSON.parse(String(v))
    return Array.isArray(parsed) && parsed.length > 0 ? parsed.join(', ') : '—'
  } catch {
    return String(v)
  }
}

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: 'Genetic Algorithm',
    fields: [
      { label: 'Population size', key: 'pop_size' },
      { label: 'Generations', key: 'generations' },
      { label: 'Early-stop patience', key: 'early_stop' },
      { label: 'Time horizon (hours)', key: 'time_horizon' },
      { label: 'Seed', key: 'seed' },
      // Null when the run used the engine default, which is 60.
      {
        label: 'Survey probes',
        key: 'survey_probe_count',
        format: (v) => (typeof v === 'number' && v > 0 ? String(v) : '60'),
      },
    ],
  },
  {
    title: 'Advanced GA',
    // Exploration vs exploitation knobs. Defaults: mutation_prob 0.4,
    // crossover_prob 0.9, mutation_rate_adaptivity 0.5, tournament_size 3.
    fields: [
      { label: 'Mutation probability', key: 'mutation_prob' },
      { label: 'Crossover probability', key: 'crossover_prob' },
      { label: 'Mutation rate adaptivity', key: 'mutation_rate_adaptivity' },
      { label: 'Tournament size', key: 'tournament_size' },
    ],
  },
  {
    title: 'Rule Strictness',
    // Labels mirror the UI inputs in ScheduleControlPanel exactly
    // (Crew Constraint / Min Run Length / Late Penalty / Changeover Cost /
    // Env Cost) so the snapshot reads identically to what the user set.
    fields: [
      {
        label: 'Allow cleaning during downtime',
        key: 'allow_cleaning_during_downtime',
        format: fmtBool,
      },
      {
        label: 'Fragment production into capped-resource gaps',
        key: 'enable_resource_yield',
        format: fmtBool,
      },
      { label: 'Crew Constraint', key: 'w_crew_constraint' },
      { label: 'Min Run Length', key: 'w_minrun' },
      { label: 'Late Penalty', key: 'w_late' },
      { label: 'Changeover Cost', key: 'w_changeover_cost' },
      { label: 'Env Cost', key: 'w_changeover_env_cost' },
      // Small-remainder demand adjustment. Worth showing precisely BECAUSE
      // it deletes demand: a planner reading a schedule that met everything
      // needs to be able to see which gates were in force when it did.
      // Undefined on any run made before these were surfaced, which the
      // renderer already shows as blank rather than as 0 - and 0 here would
      // read as 'the rule was disabled', a different schedule entirely.
      { label: 'Small-Run Drop (hrs)', key: 'small_batch_drop_hours' },
      {
        label: 'Small-Run Drop (max fraction)',
        key: 'small_batch_drop_max_fraction',
      },
      // Strictness toggles. "Yes" = free (default); "No" = strict
      // (missing rule drops the product from the schedule).
      {
        label: 'Allow missing base-material changeover',
        key: 'allow_missing_base_material_changeover',
        format: fmtBool,
      },
      {
        label: 'Allow missing product changeover',
        key: 'allow_missing_product_changeover',
        format: fmtBool,
      },
      // Internal weights — not surfaced in the UI but recorded for
      // completeness. Could be hidden behind a "show advanced" toggle later.
      { label: 'Global makespan weight', key: 'w_global_makespan' },
      { label: 'Line makespan weight', key: 'w_line_makespan' },
      { label: 'Schedule end weight', key: 'w_schedule_end' },
    ],
  },
  {
    title: 'Business Priorities',
    // Labels match the UI slider headers (Throughput / Service / Ops / Cost
    // / Balance).
    fields: [
      { label: 'Throughput', key: 'w_user_throughput' },
      { label: 'Service', key: 'w_user_service' },
      { label: 'Ops / Cost', key: 'w_user_ops' },
      { label: 'Balance', key: 'w_user_balance' },
    ],
  },
  {
    title: 'Other',
    fields: [
      { label: 'Fix before hour', key: 'fix_before_hour' },
      {
        label: 'Not-produced keys',
        key: 'not_produced_keys',
        format: fmtList,
      },
      { label: 'Min start hour', key: 'min_start_hour' },
      { label: 'Batch product order', key: 'batch_product_order' },
    ],
  },
]

const formatTimestamp = (unix: number): string => {
  if (!unix) return '—'
  return new Date(unix * 1000).toLocaleString()
}

const statusChipColor = (
  status: string
): 'success' | 'error' | 'warning' | 'default' => {
  if (status === 'complete') return 'success'
  if (status === 'failed') return 'error'
  if (status === 'running') return 'warning'
  return 'default'
}

/**
 * Read-only display of the settings snapshot a given iteration was run with.
 * Drops into ScheduleControlPanel (or any results page) alongside the
 * scheduler outputs. Renders nothing while loading, an empty-state when no
 * run has happened, and the full grouped breakdown when a row is present.
 */
export const ScheduleSettingsSnapshotView = ({
  schedulerId,
  iteration,
}: ScheduleSettingsSnapshotProps) => {
  const { data, isLoading, isError } = useScheduleSettings(
    schedulerId,
    iteration
  )

  // "Save to SOR" — promote this run's snapshot to the tenant
  // ConfigurationSettings table so future schedules pick it up as their
  // default. Only the fields in ConfigurationSettings are written; per-run
  // metadata (run_status, timestamps, fix_before_hour, etc.) is dropped.
  const { mutate: saveToSor, isLoading: isSavingToSor } =
    useUpdateConfigurationSettings(
      () => toast.success('Saved as standard configuration settings'),
      () => toast.error('Failed to save configuration settings')
    )

  const handleSaveToSor = () => {
    if (!data?.data) return
    const snap = data.data
    const payload: ConfigurationSettings = {
      pop_size: snap.pop_size,
      generations: snap.generations,
      early_stop: snap.early_stop,
      seed: snap.seed,
      mutation_prob: snap.mutation_prob,
      crossover_prob: snap.crossover_prob,
      mutation_rate_adaptivity: snap.mutation_rate_adaptivity,
      tournament_size: snap.tournament_size,
      allow_cleaning_during_downtime: snap.allow_cleaning_during_downtime,
      enable_resource_yield: snap.enable_resource_yield,
      allow_missing_base_material_changeover:
        snap.allow_missing_base_material_changeover,
      allow_missing_product_changeover: snap.allow_missing_product_changeover,
      w_changeover_cost: snap.w_changeover_cost,
      w_changeover_env_cost: snap.w_changeover_env_cost,
      w_crew_constraint: snap.w_crew_constraint,
      w_minrun: snap.w_minrun,
      w_late: snap.w_late,
      w_user_throughput: snap.w_user_throughput,
      w_user_service: snap.w_user_service,
      w_user_ops: snap.w_user_ops,
      w_user_balance: snap.w_user_balance,
      batch_product_order:
        snap.batch_product_order as ConfigurationSettings['batch_product_order'],
      // Four fields `ConfigurationSettings` requires that the snapshot may
      // not carry, because the API only began writing them later. Falling
      // back to the engine's own defaults rather than to 0: for the two
      // small-run gates 0 does not mean "unset", it means "disable the rule",
      // so a Save to SOR that coerced an absent value to 0 would silently
      // turn the rule off for the whole tenant.
      // These two are NOT in the snapshot at all - `write_settings_delta`
      // never wrote them - so there is nothing to carry across and the SOR
      // default is the only honest value. Reading them off `snap` would look
      // like preservation while always yielding undefined.
      w_clean_count: 0,
      tail_buffer_hours: 400,
      small_batch_drop_hours: snap.small_batch_drop_hours ?? 2,
      small_batch_drop_max_fraction: snap.small_batch_drop_max_fraction ?? 0.1,
      survey_probe_count: snap.survey_probe_count ?? 60,
    }
    saveToSor(payload)
  }

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading settings snapshot…
        </Typography>
      </Paper>
    )
  }

  if (isError) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Could not load settings snapshot.
        </Typography>
      </Paper>
    )
  }

  if (!data?.exists || !data.data) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No settings snapshot yet — run the scheduler to record which settings
          produced the result.
        </Typography>
      </Paper>
    )
  }

  const snap = data.data

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Settings used for this run
        </Typography>
        <Chip
          size="small"
          label={snap.run_status}
          color={statusChipColor(snap.run_status)}
        />
        <Typography variant="caption" color="text.secondary">
          {formatTimestamp(snap.run_timestamp_unix)}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="outlined"
          onClick={handleSaveToSor}
          disabled={isSavingToSor}
          sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1 }}
        >
          {isSavingToSor ? 'Saving…' : 'Save to SOR'}
        </Button>
      </Stack>

      {SECTIONS.map((section) => (
        <Box key={section.title} sx={{ mb: 2 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              letterSpacing: 0.8,
              fontWeight: 600,
            }}
          >
            {section.title}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(180px, 1fr) auto',
              rowGap: 0.5,
              columnGap: 2,
            }}
          >
            {section.fields.map((field) => {
              const raw = snap[field.key]
              const display = field.format ? field.format(raw) : fmtNumber(raw)
              return (
                <>
                  <Typography
                    key={`${field.key}-label`}
                    variant="body2"
                    color="text.secondary"
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    key={`${field.key}-value`}
                    variant="body2"
                    sx={{ fontFamily: 'monospace', textAlign: 'right' }}
                  >
                    {display}
                  </Typography>
                </>
              )
            })}
          </Box>
        </Box>
      ))}
    </Paper>
  )
}
